import React, { useCallback, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { withAuth } from "../contexts/auth";
import { withTracking } from "../contexts/tracking";
import { Avatar, ImageWithFallback, Popover, PopoverMenuContent, PopoverMenuContentItem, VerticalMenuBar } from "../../components";
import { useRecoilValue } from "recoil";
import { useResetAtom } from "jotai/utils";
import {
  allProductPropertiesState,
  organizationState,
  permissionsState,
  userFeaturesPermissionsState,
  userState,
} from "../../atoms";
import { getImgLocation, setCookie } from "../../utils";
import { useAtomValue } from "jotai";
import { PAGE_ACCESS } from "../../constants/featuresAndPermissions";
import { useLogout } from "../../hooks";
import { currentOrgIdCookie } from "../../utils/cookies";
import { UnitsPopoverContent, unitTypes } from "./UnitsPopover";

const SideMenu = ({ tracking, auth }) => {

  // hooks
  const history = useHistory();
  const location = useLocation();
  const logout = useLogout();

  // local states
  const [isMainMenuOpen, setMainMenuOpen] = useState(false);

  // global states
  const organization = useRecoilValue(organizationState);
  const { isVendor, isPlanner, id: orgId, displayName: orgName } = organization;
  const { organizations = [] } = useRecoilValue(userState);
  const resetAllProductPropertiesState = useResetAtom(allProductPropertiesState);
  const { pageAccess } = useAtomValue(userFeaturesPermissionsState);
  const { userRoles = [] } = useAtomValue(permissionsState);

  const isMasterOrganization = useMemo(() => isPlanner || isVendor, [isPlanner, isVendor]);

  const orgLogoUrl = useMemo(() => getImgLocation(organization?.logo)?.url, [organization]);

  const onLogout = useCallback(() => {
    logout();
  }, [logout]);

  const onChangeOrganization = useCallback((id) => {
    resetAllProductPropertiesState();
    setCookie(currentOrgIdCookie, id);

    window.location = "/products";
  }, [resetAllProductPropertiesState]);

  const onClickNavLink = useCallback((linkData) => {
    tracking && tracking.track(linkData.trackName);
  }, [tracking]);

  return (
    <VerticalMenuBar
      className="bimmatch-side-menu"
      top={
        <Popover
          content={
            <MainMenuPopoverContent
              pageAccess={pageAccess}
              currentPath={location.pathname}
              onClickNavLink={onClickNavLink}
              history={history}
            />
          }
          placement="right"
          align="start"
          hideArrow={true}
          className="side-menu-main-popover"
          onClick={setMainMenuOpen}
          onClickOutside={() => setMainMenuOpen(false)}
        >
          <div className={`side-menu-logo-button ${isMainMenuOpen ? "side-menu-logo-open" : ""}`} title="Menu">
            <img className="menu-lines-icon" alt="" src="/images/icon/hamburger-menu.svg" />
            <img className="logo-default" alt="bimmatch" src="/images/logo.svg" />
            <img className="logo-hover" alt="bimmatch" src="/images/logo-white.svg" />
          </div>
        </Popover>
      }
      bottom={
        auth && auth.isAuthenticated && (
          <>
            {!!organizations.length && (
              <Popover
                content={
                  <OrgPopoverContent
                    organizations={organizations}
                    currentOrgId={orgId}
                    userRoles={userRoles}
                    onChangeOrganization={onChangeOrganization}
                    isMasterOrganization={isMasterOrganization}
                    showOrganizationAccountTab={!!pageAccess?.[PAGE_ACCESS.ORGANIZATION_SETTINGS]}
                    onClickOrgAccount={() => history.push("/settings/organization")}
                  />
                }
                placement="right"
                align="end"
                hideArrow={true}
                className="side-menu-org-popover"
              >
                <div className="side-menu-org-button" title={orgName || "Organization"}>
                  <img className="org-icon" alt="" src="/images/icon/organization.svg" />
                  {orgLogoUrl ? (
                    <ImageWithFallback className="org-logo" alt={orgName || "organization"} src={orgLogoUrl} />
                  ) : (
                    <span className="org-initials">{getOrgInitials(orgName)}</span>
                  )}
                </div>
              </Popover>
            )}
            <Popover
              content={
                <PopoverMenuContent
                  items={[
                    {
                      id: "my-account",
                      src: "/images/icon/user-account.svg",
                      label: "My account",
                      withHr: true,
                      onClick: () => history.push("/settings/personal"),
                    },
                    {
                      id: "logout",
                      src: "/images/icon/logout.svg",
                      label: "Log out",
                      onClick: onLogout,
                    },
                  ]}
                />
              }
              placement="right"
              align="end"
              hideArrow={true}
              className="side-menu-profile-popover"
            >
              <div className="side-menu-profile-button">
                <Avatar url={auth?.user?.photoURL} fullName={getUserName(auth)} />
              </div>
            </Popover>
          </>
        )
      }
    />
  );
};

const allMenuLinks = [
  {
    path: "projects",
    access: PAGE_ACCESS.PROJECTS,
    name: "Projects",
    trackName: "Poject List",
  },
  {
    path: "docs",
    access: PAGE_ACCESS.SPECS,
    name: "Docs",
    trackName: "Docs New",
  },
  {
    path: "data-sets",
    access: PAGE_ACCESS.DATA_SETS,
    name: "Data-sets",
    trackName: "Data-sets",
  },
  {
    path: "organization-items",
    access: PAGE_ACCESS.ORGANIZATION_DB,
    name: "Objects",
    trackName: "Organization DB",
  },
  {
    path: "products",
    access: PAGE_ACCESS.PRODUCTS,
    name: "Market products",
    trackName: "Products List",
  },
  {
    path: "team",
    access: PAGE_ACCESS.TEAM,
    name: "Team",
    trackName: "Team",
  },
  // {
  //   path: "analytics",
  //   access: PAGE_ACCESS.ANALYTICS,
  //   name: "ANALYTICS",
  //   trackName: "Analytics"
  // },
  // {
  //   path: "specs",
  //   access: PAGE_ACCESS.SPECS,
  //   name: "DOCS",
  //   trackName: "Docs"
  // },
];

const MainMenuPopoverContent = ({ pageAccess, currentPath, onClickNavLink, history }) => {

  const [isUnitsSubmenuOpen, setUnitsSubmenuOpen] = useState(false);

  const selectedUnit = useMemo(
    () => unitTypes.find((unitData) => unitData.id === (localStorage.getItem("unit") || "metric")),
    []
  );

  const onSelectUnit = useCallback((unitData) => {
    if (selectedUnit.id !== unitData.id) {
      localStorage.setItem("unit", unitData.id);
      history.go(0);
    }
  }, [selectedUnit, history]);

  const visibleLinks = useMemo(
    () => allMenuLinks.filter((linkData) => !!pageAccess?.[linkData.access]),
    [pageAccess]
  );

  const onClickUnitsItem = useCallback((event) => {
    // the portaled content bubbles synthetic events up the React tree to the
    // popover trigger, whose onClick would toggle the whole menu closed
    event.stopPropagation();
    setUnitsSubmenuOpen((prev) => !prev);
  }, []);

  return (
    <div className="side-menu-main-content">
      {!!visibleLinks.length && (
        <>
          <ul className="side-menu-nav-list">
            {visibleLinks.map((linkData) => (
              <li key={linkData.path}>
                <Link
                  to={`/${linkData.path}`}
                  className={currentPath === `/${linkData.path}` ? "active" : ""}
                  onClick={() => onClickNavLink(linkData)}
                >
                  {linkData.name}
                </Link>
              </li>
            ))}
          </ul>
          <hr />
        </>
      )}
      <div
        className="side-menu-units-item"
        data-popover-keep-open="true"
        onClick={onClickUnitsItem}
      >
        <img className="unit-icon" alt="unit" src={`/images/icon/${selectedUnit.id}-unit.svg`} />
        <span className="unit-name">{selectedUnit.displayName}</span>
        <img className="submenu-arrow" alt="" src="/images/icon/arrow-right.svg" />
        {isUnitsSubmenuOpen && (
          <div className="side-menu-units-submenu" onClick={(event) => event.stopPropagation()}>
            <UnitsPopoverContent selectedUnit={selectedUnit.id} onSelectUnit={onSelectUnit} />
          </div>
        )}
      </div>
    </div>
  );
};

const OrgPopoverContent = ({
  organizations,
  currentOrgId,
  userRoles,
  onChangeOrganization,
  isMasterOrganization,
  showOrganizationAccountTab,
  onClickOrgAccount,
}) => {

  const getRoles = useCallback((organization) => {
    return userRoles.filter((role) => organization?.roles.includes(role.id));
  }, [userRoles]);

  const onClickOrganizationTab = useCallback((event, orgId) => {
    event.stopPropagation();
    if (currentOrgId !== orgId)
      onChangeOrganization && onChangeOrganization(orgId);
  }, [currentOrgId, onChangeOrganization]);

  return (
    <div className="org-popover-content">
      <div className="organizations-list">
        {organizations.map((organization) => (
          <div
            key={organization.id}
            className={`organization-details ${(organization.id === currentOrgId) ? "organization-details-active" : ""} ${!organization?.roles?.length ? "organization-details-disabled" : ""}`}
            onClick={(event) => onClickOrganizationTab(event, organization.id)}
          >
            <div className="organization-name">
              {organization?.displayName || (<span className="no-org-name">{isMasterOrganization ? "Missing organization name" : "No organization"}</span>)}
            </div>
            {!!organization?.roles?.length && (
              <div className="organization-roles">
                {getRoles(organization).map((role) => (
                  <p key={role.id} className="organization-role-name">
                    {role?.displayName}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showOrganizationAccountTab && (
        <>
          <hr />
          <PopoverMenuContentItem
            id="org-account"
            src="/images/icon/organization.svg"
            label="Organization account"
            onClick={onClickOrgAccount}
          />
        </>
      )}
    </div>
  );
};

const getUserName = (auth) => {
  if (auth?.user?.displayName) return auth.user.displayName;
  if (localStorage.getItem("username")) return localStorage.getItem("username");
  return auth?.user?.email?.split("@")[0];
};

const getOrgInitials = (orgName) => {
  if (!orgName) return "OR";
  const words = orgName.trim().split(" ").filter(Boolean);
  const initials = (words.length > 1) ? `${words[0].charAt(0)}${words[1].charAt(0)}` : words[0].substring(0, 2);
  return initials.toUpperCase();
};

export default withTracking(withAuth(SideMenu));
